"use client";

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import GradientBlobContainer from "@/components/ui/container/GradientBlobContainer";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button/Button";
import { useAuth } from "@/context/AuthContext";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import * as AgenticV2API from "@/services/api/agentic-v2";
import {
  clearOpenworkDemoState,
  closeOpenworkReseedConfirm,
  configureOpenworkDemoStorage,
  setOpenworkReseeding,
  useOpenworkReseedConfirmOpen,
  useOpenworkReseeding,
} from "./openworkDemoState";

/** Above OS windows (z-index ~100+) and dock chrome. */
const DEMO_WORKSPACE_OVERLAY_Z = "z-[10050]";

function formatResetError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
      if (typeof detail === "string" && detail.trim()) return detail;
      if (Array.isArray(detail)) {
        const message = detail
          .map((row) => (typeof row === "object" && row && "msg" in row ? String(row.msg) : String(row)))
          .filter(Boolean)
          .join(", ");
        if (message) return message;
      }
      return `Reset failed (${error.response.status})`;
    }
    if (error.request) {
      return "Could not reach the server. Check your connection and try again.";
    }
  }
  return error instanceof Error ? error.message : "Reset failed";
}

type OpenworkReseedConfirmDialogProps = {
  onConfirm: () => void;
  disabled?: boolean;
};

function OpenworkReseedConfirmDialog({
  onConfirm,
  disabled = false,
}: OpenworkReseedConfirmDialogProps) {
  return (
    <div
      className={`pointer-events-auto absolute inset-0 ${DEMO_WORKSPACE_OVERLAY_Z} flex items-center justify-center p-4`}
      data-testid="openwork-reseed-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="openwork-reseed-confirm-title"
    >
      <Button
        plain
        size="unstyled"
        type="button"
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        aria-label="Close"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          closeOpenworkReseedConfirm();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-gradient-to-bl from-slate-800/95 to-slate-700/95 p-6 shadow-2xl shadow-black/50 ring-1 ring-white/10 backdrop-blur-md">
        <h2
          id="openwork-reseed-confirm-title"
          className="text-lg font-medium text-white"
        >
          Reset your test session?
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          This restores your three starting letters and clears any addendums, edits, or notifications
          from this session. Open aniOS windows will close. You&apos;ll see the welcome screen again
          when it finishes.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            plain
            onClick={() => closeOpenworkReseedConfirm()}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button color="indigo" disabled={disabled} onClick={onConfirm}>
            Reset session
          </Button>
        </div>
      </div>
    </div>
  );
}

type OpenworkReseedingScreenProps = {
  error?: string | null;
  onDismissError: () => void;
};

function OpenworkReseedingScreen({ error, onDismissError }: OpenworkReseedingScreenProps) {
  return (
    <div
      className={`pointer-events-auto absolute inset-0 ${DEMO_WORKSPACE_OVERLAY_Z} flex min-h-0 flex-col overflow-hidden bg-slate-950`}
      data-testid="openwork-reseed-overlay"
      role="status"
      aria-live="polite"
      aria-busy={!error}
    >
      <div className="pointer-events-none absolute inset-0 opacity-50" aria-hidden>
        <GradientBlobContainer
          seed="letter-review-demo"
          dark
          position="bl"
          className="h-full w-full"
          eager
          rasterize
          themeColors={["#312e81", "#1e3a8a", "#0f172a", "#4c1d95"]}
        />
      </div>
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.12)_0%,transparent_55%)]"
        aria-hidden
      />

      <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10">
        <motion.div
          className="mb-8"
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Logo
            iconOnly
            className="mx-auto size-24"
            data-testid="openwork-reseed-ani-logo"
          />
        </motion.div>

        {error ? (
          <div className="max-w-md text-center">
            <p className="text-base text-rose-300">{error}</p>
            <Button className="mt-6" color="indigo" onClick={onDismissError}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <p className="text-base text-slate-200">Resetting your account</p>
            <div
              className="mt-4 flex items-center justify-center gap-1.5 py-2"
              aria-hidden
            >
              {[0, 1, 2].map((index) => (
                <motion.span
                  key={index}
                  className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-300"
                  animate={{ opacity: [0.25, 1, 0.25] }}
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.2,
                  }}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Just a moment — resetting your letters and notifications.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** Letter-review demo overlays — reset confirm and loading only. */
export function OpenworkOsDemoLayer() {
  const { user } = useAuth();
  const osActions = useOsActionsOptional();
  const isDemoTenant = (user?.company_id ?? "").toLowerCase() === "openworks";
  const isReseeding = useOpenworkReseeding();
  const reseedConfirmOpen = useOpenworkReseedConfirmOpen();
  const [reseedError, setReseedError] = useState<string | null>(null);

  useEffect(() => {
    if (!isDemoTenant) return;
    configureOpenworkDemoStorage(user?.email ?? user?.id ?? null);
  }, [isDemoTenant, user?.email, user?.id]);

  const handleDismissReseedError = useCallback(() => {
    setReseedError(null);
    setOpenworkReseeding(false);
  }, []);

  const handleResetSession = useCallback(() => {
    void (async () => {
      closeOpenworkReseedConfirm();
      setReseedError(null);
      setOpenworkReseeding(true);
      osActions?.closeAllWindows();

      try {
        await AgenticV2API.reseedOpenworkLetterReviewDemo();
        configureOpenworkDemoStorage(user?.email ?? user?.id ?? null);
        await clearOpenworkDemoState();
        osActions?.closeAllWindows();
        osActions?.openWindow({ appId: "home", title: "Home", params: {} });
        window.dispatchEvent(new CustomEvent("openwork-demo-reseeded"));
        setOpenworkReseeding(false);
      } catch (e) {
        setReseedError(formatResetError(e));
      }
    })();
  }, [osActions, user?.email, user?.id]);

  if (!isDemoTenant) return null;

  return (
    <>
      {isReseeding ? (
        <OpenworkReseedingScreen error={reseedError} onDismissError={handleDismissReseedError} />
      ) : null}
      {!isReseeding && reseedConfirmOpen ? (
        <OpenworkReseedConfirmDialog onConfirm={handleResetSession} />
      ) : null}
    </>
  );
}
